/**
 * 계정 탄창 - 라운드가 아니라 **계정**이 들고 있는 수.
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제)
 *
 * ## `?mags=`가 `Tuning`에 없는 이유
 *
 * 탄창은 라운드 규칙이 아니라 **계정 저장**에 사는 값이라 `Tuning`에 섞으면 "저장된 값과
 * 튜닝 값 중 무엇이 진실인가"라는 질문이 생긴다(`tuning.parseMagazineOverride` 주석).
 * 그래서 이 키는 저장을 **읽지도 쓰지도 않고** 이번 세션의 보유량만 갈아 끼운다 -
 * `?mags=0`으로 대조군을 돌린 다음 주소에서 키만 지우면 원래 보유량이 그대로 돌아온다.
 * 지워지는 대조군이 아니라 되돌아오는 대조군이라야 도구다.
 *
 * ## 왜 모듈 하나인가
 *
 * 이 값에는 입구가 셋 있다(라운드가 쓴다 · 개발 훅이 준다 · `?mags=`가 덮는다). 셋이 전부
 * "저장을 건드리는가"를 스스로 판단해야 하는데, 그 판단이 흩어지면 **한 줄만 새어 나가도
 * 대조군이 진짜 값을 먹는다.** 강제 여부를 아는 곳을 하나로 묶는 것이 이 파일의 존재 이유다.
 *
 * 진행 저장 자체는 `main.ts`가 쥔다(`getProgress`/`setProgress`) - 진행에는 단계 기록도
 * 들어 있어서 여기가 통째로 소유하면 결과 저장까지 딸려 오고, 그러면 "탄창 파일"이 아니게 된다.
 */

import {
  consumeMagazine,
  createMagazineState,
  grantMagazines,
  type MagazineSource,
  type MagazineState,
} from '../game/magazines'
import { commitMagazines, type Progress } from '../game/progress'
import { parseMagazineOverride } from '../game/tuning'

export type AccountMagazines = Readonly<{
  /** 계정이 지금 들고 있는 수. 라운드 안의 수(`state.magazines`)와 갈릴 수 있다(`?mags=`). */
  count: () => number
  /** 라운드가 하나를 썼다. */
  spend: (remaining: number) => void
  /**
   * 지급. 봉투(`'envelope'`)와 개발 훅(`'debug'`)이 부른다. **실제로 들어간 수**를 돌려준다 -
   * 상한(99)에 걸리면 요청보다 적고, 그 차이가 핀으로 바뀌는 근거다(`economy/inventory.ts`의 `applyReward`가
   * 같은 계산을 먼저 하지만, 계정 쪽 진실은 여기다. 둘이 어긋나면 그 자체가 보고할 값이다).
   */
  grant: (count: number, source: MagazineSource) => number
}>

export type AccountMagazinesDeps = Readonly<{
  /** 주소창 질의문. `?mags=`만 읽는다. */
  search: string
  getProgress: () => Progress
  setProgress: (next: Progress) => void
}>

export function createAccountMagazines(deps: AccountMagazinesDeps): AccountMagazines {
  /**
   * `?mags=N`으로 보유량이 강제된 세션인가.
   *
   * 참이면 **저장을 건드리지 않는다.** 대조군을 돌리려고 `?mags=0`을 한 번 열었다가
   * 진짜 보유량이 0으로 굳으면 그건 도구가 아니라 사고다. 주소에서 키를 지우면
   * 원래 값이 그대로 돌아오는 것이 이 스위치의 계약이다(파일 머리말).
   */
  const override = parseMagazineOverride(deps.search)
  const forced = override !== undefined

  /**
   * 이번 세션이 믿는 계정 보유량.
   *
   * `progress.magazines`를 그대로 쓰지 않고 변수를 하나 더 두는 이유가 강제 모드다 -
   * 강제 중에는 화면과 라운드가 이 값을 따르고 저장은 원래 값을 그대로 들고 있어야 한다.
   * 강제가 아니면 둘은 언제나 같은 값이며, `spend`가 한 걸음씩 같이 옮긴다.
   */
  let magazines: MagazineState =
    override === undefined ? deps.getProgress().magazines : createMagazineState(override)

  /**
   * 라운드가 탄창 하나를 실제로 썼다. **소비 규칙은 `magazines.ts`가 소유한다** -
   * 여기서 `remaining`을 그대로 대입하지 않고 `consumeMagazine`을 부르는 이유가 그것이다.
   * 대입하면 상한·이력 규칙이 두 벌이 되고, 무엇보다 라운드가 보내 온 숫자를 검증 없이 믿게 된다.
   *
   * @param remaining 라운드가 계산한 잔여량. 계정 쪽과 어긋나면 그 자체가 보고할 값이다 -
   *                  `?mags=`로 라운드에 더 큰 값을 넣고 돌리면 정확히 이 어긋남이 생긴다.
   */
  function spend(remaining: number): void {
    const consumed = consumeMagazine(magazines)
    if (!consumed.ok) {
      console.warn(
        `[main] 계정에 탄창이 없는데 라운드가 하나를 썼다(라운드 잔여 ${String(remaining)}개). ` +
          '`?mags=`로 보유량을 덮어쓴 세션이면 정상이다.',
      )
      return
    }

    magazines = consumed.state
    // 강제 모드는 저장을 읽지도 쓰지도 않는다. 여기서 한 줄만 새어 나가도 대조군이 진짜 값을 먹는다.
    if (forced) return

    const committed = commitMagazines(deps.getProgress(), magazines)
    deps.setProgress(committed.progress)
    if (!committed.saved) {
      console.warn('[main] 탄창 소비를 저장하지 못했다. 이번 세션에서는 줄어든 채로 간다.')
    }
  }

  /**
   * 탄창을 지급한다. 봉투(`'envelope'`)가 첫 실제 지급 경로이고, 개발 훅(`'debug'`)이 그 옆에 있다
   * (광고·결제·미니게임은 아직 없다). 돌려주는 값은 **실제로 들어간 수**다.
   *
   * 지급은 계정에 즉시 반영되지만 **돌고 있는 라운드에는 들어가지 않는다.** 라운드의 탄창 수는
   * 무대를 세울 때 한 번 들어가고 그 뒤로는 리듀서만 줄이기 때문이다(`retry`·`restart`도
   * 라운드 값을 이어받는다). 그래서 새 값이 HUD에 뜨는 시점은 **다음 무대**다 -
   * 봉투는 단계 선택 화면에서 열리므로(로드맵 M3 - 결과 화면에는 여는 버튼이 없다) 이 시차가
   * 사람 눈에 보이지 않는다. 다음 무대의 HUD 탄창 칩 숫자가 늘어 있는 것이 정상 경로다.
   *
   * 이 시차는 광고를 붙이는 날 반드시 걸린다("광고를 봤는데 지금 판에 안 들어온다").
   * 그때 필요한 것은 여기서 상태를 몰래 갈아 끼우는 것이 아니라 `gameState`에 지급 이벤트를
   * 하나 더 여는 것이다 - 라운드 상태를 리듀서 밖에서 만지는 순간 결정론이 끊긴다.
   */
  function grant(count: number, source: MagazineSource): number {
    const before = magazines.count
    magazines = grantMagazines(magazines, count, source)
    if (!forced) {
      const committed = commitMagazines(deps.getProgress(), magazines)
      deps.setProgress(committed.progress)
      if (!committed.saved) console.warn('[main] 탄창 지급을 저장하지 못했다.')
    }
    return magazines.count - before
  }

  return {
    count: () => magazines.count,
    spend,
    grant,
  }
}
